type IdOrSlug = string | number;

const settingsHome = () => '/app/settings';

export const appRouting = {
  org: (organizationId: string = ':app_id') => {
    const orgBase = `/app/${organizationId}`;
    const accountHome = () => `${settingsHome()}/account`;
    const organization = () => `${settingsHome()}/organization`;

    return {
      organization: {
        root: () => orgBase,
        dashboard: () => `${orgBase}/dashboard`,
        organizationTalents: () => `${orgBase}/organization-talents`
      },
      settings: {
        personal: {
          account: () => `${accountHome()}/details`,
          security: () => `${accountHome()}/security`
        },
        global: {
          users: () => `${organization()}/users`,
          permissionGroups: () => `${organization()}/roles`,
          authentication: () => `${organization()}/auth`,
          integrations: () => `${organization()}/integrations`,
          workspace: () => `${organization()}/workspace`,
          workspaceSection: (section: string = ':section') =>
            `${organization()}/workspace/${section}`,
          manageEnterprise: () => `${organization()}/enterprise`,
          visitorManagement: () => `${organization()}/visitor-management`,
          resourceTypes: () => `${organization()}/resource-types`,
          resourceGroups: (id?: IdOrSlug) =>
            `${organization()}/resource-types/groups${id == null ? '' : `/${id}`}`,
          tags: () => `${organization()}/tags`,
          rateCards: () => `${organization()}/rate-cards`,
          expenseTypes: () => `${organization()}/expense-types`,
          slotCategories: () => `${organization()}/slot-categories`,
          resourceType: (id: IdOrSlug = ':resourceTypeId') =>
            `${organization()}/resource-types/${id}`,
          resourceTypeTab: (
            id: IdOrSlug = ':resourceTypeId',
            tab:
              | 'details'
              | 'fields'
              | 'workspaces'
              | 'groups'
              | ':tab' = ':tab'
          ) => `${organization()}/resource-types/${id}/${tab}`,
          resourceTypeFieldsArchived: (id: IdOrSlug = ':resourceTypeId') =>
            `${organization()}/resource-types/${id}/fields/archived`
        },
        workspace: (workspaceId: IdOrSlug = ':workspaceId') => {
          const wsId = String(workspaceId);
          const root = `${settingsHome()}/w/${wsId}`;
          return {
            root: () => root,
            general: () => `${root}/general`,
            users: () => `${root}/users`,
            permissionGroups: () => `${root}/permission-groups`,
            resourceTypes: () => `${root}/resource-types`,
            resourceType: (id: IdOrSlug = ':resourceTypeId') =>
              `${root}/resource-types/${id}`,
            resourceTypeFieldsArchived: (id: IdOrSlug = ':resourceTypeId') =>
              `${root}/resource-types/${id}/fields/archived`,
            resourceDuplicates: () => `${root}/resource-duplicates`,
            bookingTypes: () => `${root}/booking-types`,
            bookingWorkflow: () => `${root}/booking-workflow`,
            confirmationTemplates: () => `${root}/confirmation-templates`,
            productionTypes: () => `${root}/production-types`,
            productionWorkflow: () => `${root}/production-workflow`,
            /** `:team_id` matches v1 settings pages that read `match.params.team_id`. */
            team: (teamId: IdOrSlug = ':team_id') => {
              const teamRoot = `${root}/teams/${teamId}`;
              return {
                root: () => teamRoot,
                publicProfile: () => `${teamRoot}/public-profile`,
                publicProfileSection: () =>
                  `${teamRoot}/public-profile/:section`,
                fields: () => `${teamRoot}/fields`,
                fieldsStructure: (structure: string = ':structure') =>
                  `${teamRoot}/fields/${structure}`,
                fieldsStructureId: (
                  structure: string = ':structure',
                  id: IdOrSlug = ':id'
                ) => `${teamRoot}/fields/${structure}/${id}`,
                statuses: () => `${teamRoot}/statuses`,
                resourceTypes: () => `${teamRoot}/resource-types`,
                pronounsOptions: () => `${teamRoot}/pronouns-options`,
                rateCards: () => `${teamRoot}/rate-cards`,
                productionTypes: () => `${teamRoot}/production-types`,
                productionTemplates: () => `${teamRoot}/production-templates`,
                productionTemplatesId: (id: IdOrSlug = ':id') =>
                  `${teamRoot}/production-templates/${id}`,
                callsheetTemplates: () => `${teamRoot}/callsheet-templates`,
                expenseTypes: () => `${teamRoot}/expense-types`,
                slotCategories: () => `${teamRoot}/slot-categories`,
                alerts: () => `${teamRoot}/alerts`,
                bookingTypes: () => `${teamRoot}/booking-types`,
                confirmationTemplates: () =>
                  `${teamRoot}/confirmation-templates`,
                imports: () => `${teamRoot}/imports`,
                importsId: (id: IdOrSlug = ':id') =>
                  `${teamRoot}/imports/${id}`,
                duplicates: () => `${teamRoot}/duplicates`,
                arrivalBoard: () => `${teamRoot}/arrival-board`,
                qrCodeMapping: () => `${teamRoot}/qr-code-mapping`
              };
            }
          };
        }
      },
      /**
       * Only paths `Routes.jsx` registers under `/w/:workspaceId` — a builder
       * here is a promise that the URL opens a page. Productions and
       * shortlists live under a team (`team()`), a booking and the calendar
       * home only on legacy URLs, reconciliations only at the organization.
       */
      workspace: (workspaceId: IdOrSlug = ':workspaceId') => {
        const wsId = String(workspaceId);
        const root = `${orgBase}/w/${wsId}`;

        return {
          /**
           * The start of every URL in this workspace, for matching one. Not a
           * page: `/w/:workspaceId` alone is not routed.
           */
          pathPrefix: () => root,
          dashboard: () => `${root}/dashboard`,
          resources: {
            root: () => `${root}/resources`,
            forResourceType: (resourceTypeId: IdOrSlug = ':resourceTypeId') =>
              `${root}/resources/${resourceTypeId}`
          },
          contacts: {
            detail: (contactId: IdOrSlug = ':id') =>
              `${root}/contacts/${contactId}`,
            tab: (contactId: IdOrSlug = ':id', tab: string = ':tab') =>
              `${root}/contacts/${contactId}/${tab}`
          },
          callouts: {
            root: () => `${root}/callouts`,
            detail: (calloutId: IdOrSlug = ':id') =>
              `${root}/callouts/${calloutId}`,
            publicCallout: (slug: string) => `${root}/p/callouts/${slug}`
          },
          bookings: {
            root: () => `${root}/bookings`,
            type: (bookingTypeId: IdOrSlug = ':bookingTypeId') =>
              `${root}/bookings/type/${bookingTypeId}`
          },
          calendar: {
            resources: {
              home: () => `${root}/calendar/resources`
            },
            productions: {
              home: () => `${root}/calendar/productions`
            }
          },
          team: (teamId: IdOrSlug = ':teamId') => {
            const teamRoot = `${root}/team/${teamId}`;
            const teamProductionsBase = `${teamRoot}/productions`;
            const teamDetailPath = (productionId: IdOrSlug = ':id') =>
              `${teamProductionsBase}/${productionId}`;
            return {
              /**
               * The start of every URL in this team, for matching one. Not a
               * page: `/team/:teamId` alone is not routed.
               */
              pathPrefix: () => teamRoot,
              productions: {
                root: () => teamProductionsBase,
                type: (productionTypeId: IdOrSlug = ':productionTypeId') =>
                  `${teamProductionsBase}/type/${productionTypeId}`,
                detail: teamDetailPath,
                resourcing: (productionId: IdOrSlug = ':id') => ({
                  root: () => `${teamDetailPath(productionId)}/resourcing`,
                  tab: (tabId: string = ':tabId') =>
                    `${teamDetailPath(productionId)}/resourcing/${tabId}`
                })
              },
              shortlists: {
                root: () => `${teamRoot}/shortlists`,
                detail: (shortlistId: IdOrSlug = ':id') =>
                  `${teamRoot}/shortlists/${shortlistId}`
              },
              callsheets: {
                root: () => `${teamRoot}/callsheets`,
                detail: (callsheetId: IdOrSlug = ':id') =>
                  `${teamRoot}/callsheets/${callsheetId}`
              },
              frontdesk: {
                root: () => `${teamRoot}/frontdesk`
              }
            };
          }
        };
      }
    };
  }
} as const;

export type AppRouting = ReturnType<typeof appRouting.org>;
export type EnterpriseWorkspaceSettingsRoutes = ReturnType<
  AppRouting['settings']['workspace']
>;
export type EnterpriseTeamSettingsRoutes = ReturnType<
  EnterpriseWorkspaceSettingsRoutes['team']
>;
export type EnterpriseWorkspaceRoutes = ReturnType<AppRouting['workspace']>;
export type EnterpriseTeamRoutes = ReturnType<
  EnterpriseWorkspaceRoutes['team']
>;
